import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Sparkles, Users } from 'lucide-react';
import {
  ActionButton,
  ContextHero,
  DataCard,
  DateBadge,
  KpiCard,
  MoneyAmount,
  PageContainer,
  SectionTitle,
  SkeletonShimmer,
  StatusBadge,
} from '@/components/design-system';
import {
  useAddB2cParticipation,
  useB2cAction,
  useB2cParticipations,
  type B2cParticipationStatut,
} from '@/hooks/queries/useB2c';

export function B2cActionDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { data: action, isLoading } = useB2cAction(id ?? null);
  const { data: parts = [] } = useB2cParticipations(id ?? null);
  const addPart = useAddB2cParticipation();

  const [nom, setNom] = useState('');
  const [tel, setTel] = useState('');
  const [prix, setPrix] = useState('');

  if (isLoading || !action) {
    return (
      <PageContainer>
        <SkeletonShimmer variant="hero" />
      </PageContainer>
    );
  }

  const caTotal = parts.reduce((s, p) => s + Number(p.prix_paye), 0);
  const remunFormateur =
    action.formateur_paiement_type === 'forfait_fixe'
      ? Number(action.formateur_montant_forfait ?? 0)
      : caTotal * (Number(action.formateur_pct_ca ?? 0) / 100);
  const benefice = caTotal - remunFormateur;

  const handleAdd = async (): Promise<void> => {
    if (!nom.trim() || !prix) {
      toast.error('Nom et prix obligatoires');
      return;
    }
    await addPart.mutateAsync({
      action_id: action.id,
      participant_nom: nom.trim(),
      participant_telephone: tel.trim() || null,
      prix_paye: Number(prix),
      mode_paiement: 'especes',
      statut_paiement: 'paye' as B2cParticipationStatut,
      present: false,
    });
    setNom('');
    setTel('');
    setPrix('');
    toast.success('Participant ajouté');
  };

  return (
    <PageContainer>
      <div className="space-y-6">
        <Link
          to="/formation-b2c"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" /> Retour aux actions B2C
        </Link>

        <ContextHero
          variant="formation_b2c"
          icon={<Sparkles className="h-5 w-5" />}
          title={action.reference}
          subtitle={
            (action.lieu ? `${action.lieu} · ` : '') +
            (action.horaire ?? new Date(action.date_action).toLocaleDateString('fr-DZ'))
          }
        />

        <div className="grid gap-3 sm:grid-cols-4">
          <KpiCard
            variant="formation_b2c"
            label="Participants"
            value={`${parts.length} / ${action.capacite_max}`}
            icon={<Users className="h-4 w-4" />}
          />
          <KpiCard
            variant="finances"
            label="CA encaissé"
            value={<MoneyAmount amount={caTotal} variant="inline" />}
          />
          <KpiCard
            variant="formation_b2c"
            label={
              action.formateur_paiement_type === 'forfait_fixe'
                ? 'Forfait formateur'
                : `Rému formateur (${action.formateur_pct_ca}%)`
            }
            value={<MoneyAmount amount={remunFormateur} variant="inline" />}
          />
          <KpiCard
            variant="echeances"
            label="Bénéfice action"
            value={<MoneyAmount amount={benefice} variant="inline" withColor />}
          />
        </div>

        <SectionTitle>Participants</SectionTitle>

        <DataCard>
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-widest text-slate-400">
              <tr>
                <th className="py-2">Nom</th>
                <th className="py-2">Téléphone</th>
                <th className="py-2">Prix payé</th>
                <th className="py-2">Statut</th>
                <th className="py-2">Présent</th>
              </tr>
            </thead>
            <tbody>
              {parts.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="py-2 font-semibold text-slate-800">{p.participant_nom}</td>
                  <td className="py-2 text-slate-600">{p.participant_telephone ?? '—'}</td>
                  <td className="py-2">
                    <MoneyAmount amount={p.prix_paye} variant="inline" />
                  </td>
                  <td className="py-2">
                    <StatusBadge
                      status={
                        p.statut_paiement === 'paye'
                          ? 'paye'
                          : p.statut_paiement === 'partiel'
                            ? 'en_cours'
                            : 'a_payer'
                      }
                    />
                  </td>
                  <td className="py-2">{p.present ? '✓' : '—'}</td>
                </tr>
              ))}
              {parts.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-3 text-center text-slate-400">
                    Aucun participant inscrit.
                  </td>
                </tr>
              )}
              <tr className="border-t border-slate-200 bg-slate-50/40">
                <td className="py-2">
                  <input
                    value={nom}
                    onChange={(e) => setNom(e.target.value)}
                    placeholder="Nom complet"
                    className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-xs"
                  />
                </td>
                <td className="py-2">
                  <input
                    value={tel}
                    onChange={(e) => setTel(e.target.value)}
                    placeholder="0555..."
                    className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-xs"
                  />
                </td>
                <td className="py-2">
                  <input
                    type="number"
                    value={prix}
                    onChange={(e) => setPrix(e.target.value)}
                    placeholder={`${action.prix_unitaire_b2c}`}
                    className="h-8 w-24 rounded-lg border border-slate-200 bg-white px-2 text-right text-xs tabular-nums"
                  />
                </td>
                <td className="py-2 text-xs text-slate-400">—</td>
                <td className="py-2 text-right">
                  <ActionButton
                    variant="gradient"
                    module="formation_b2c"
                    size="sm"
                    icon={<Plus className="h-3 w-3" />}
                    onClick={() => void handleAdd()}
                    loading={addPart.isPending}
                  >
                    Ajouter
                  </ActionButton>
                </td>
              </tr>
            </tbody>
          </table>
        </DataCard>

        {/* Hidden ref to silence unused import */}
        <span className="hidden">
          <DateBadge date={action.date_action} />
        </span>
      </div>
    </PageContainer>
  );
}
